import { AxiosError } from 'axios';
import { useState } from 'react';

import { useLogout } from 'src/hooks/useLogout';
import { useAxios } from './useAxios';
import { UseApiProps, UseApiObjectProps, BaseApiResponse } from './useApi.types';
import { useStoreState } from 'src/stores';

export const useApi: UseApiProps = <ResponseDTO>(hookParams?: UseApiObjectProps<ResponseDTO>) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError>();
  const [data, setData] = useState<ResponseDTO | undefined>();
  const isLoggedIn = useStoreState(state => state.user.data.isLogedIn);

  const { logout } = useLogout();
  const { get, post, delete: del } = useAxios();

  const handleResponse = (axiosResponseData: BaseApiResponse<ResponseDTO>) => {
    const { isSuccess, data } = axiosResponseData;

    if (!isSuccess) return;

    setData(data);
    setError(undefined);

    hookParams?.onComplete?.(data);
  };

  const handleError = (error: AxiosError) => {
    setError(error);
    setData(undefined);

    hookParams?.onError?.(error);

    if (error?.response?.status === 401) {
      isLoggedIn && logout();
    }
  };

  const getRequest = <T>(url: string, params?: T) => {
    setLoading(true);
    get<BaseApiResponse<ResponseDTO>>(url, params)
      .then(axiosResponse => {
        handleResponse(axiosResponse.data);
      })
      .catch(err => {
        handleError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const postRequest = <T>(url: string, data?: T, timeout?: number) => {
    setLoading(true);
    post<BaseApiResponse<ResponseDTO>>(url, data, timeout)
      .then(axiosResponse => {
        handleResponse(axiosResponse.data);
      })
      .catch(err => {
        handleError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteRequest = (url: string) => {
    setLoading(true);
    del<BaseApiResponse<ResponseDTO>>(url)
      .then(axiosResponse => {
        handleResponse(axiosResponse.data);
      })
      .catch(err => {
        handleError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return [
    { getRequest, postRequest, deleteRequest },
    { loading, data, error },
  ];
};
